import { useEffect, useMemo, useState } from 'react';
import type { Activity, GraduateYear, NewsPost, PublicApi, PublicHome } from '@htl/api';

export type PublicFallbacks = {
  home: PublicHome;
  activities: Activity[];
  graduates: GraduateYear[];
  news: NewsPost[];
};

export type ContentState<T> = {
  data: T;
  loading: boolean;
  source: 'api' | 'temporary';
  error?: Error;
};

function useContent<T>(fallback: T, loader?: () => Promise<T>): ContentState<T> {
  const [state, setState] = useState<ContentState<T>>({ data: fallback, loading: Boolean(loader), source: 'temporary' });

  useEffect(() => {
    let active = true;
    if (!loader) return () => { active = false; };
    loader()
      .then((data) => active && setState({ data, loading: false, source: 'api' }))
      .catch((error: Error) => active && setState({ data: fallback, loading: false, source: 'temporary', error }));
    return () => { active = false; };
  }, [fallback, loader]);

  return state;
}

export function createPublicHooks(api: PublicApi | undefined, fallback: PublicFallbacks) {
  return {
    useHome: () => useContent(fallback.home, api ? api.home : undefined),
    useActivities: () => useContent(fallback.activities, api ? api.activities : undefined),
    useGraduates: () => useContent(fallback.graduates, api ? api.graduates : undefined),
    useNews: () => useContent(fallback.news, api ? async () => (await api.news()).data : undefined),
    usePost: (slug: string) => {
      const local = useMemo(() => fallback.news.find((post) => post.slug === slug), [slug]);
      return useContent(local, api ? () => api.post(slug) : undefined);
    },
  };
}
